import { Link } from "react-router-dom";
import MarketingNavbar from "@/components/MarketingNavbar";
import { Container } from "@/components/shared/Section";
import { cn } from "@/lib/utils";

const footerColumns = [
  {
    title: "Product",
    links: [
      { label: "Features", href: "/#capabilities" },
      { label: "Pricing", href: "/#pricing" },
      { label: "Solutions", href: "/solutions" },
      { label: "Meta Ads Monitoring", href: "/solutions/meta-ads" },
      { label: "TikTok Ads Monitoring", href: "/solutions/tiktok-ads" },
      { label: "Google Ads Monitoring", href: "/solutions/google-ads" },
    ],
  },
  {
    title: "Free Tools",
    links: [
      { label: "Meta Ads Search", href: "/tools/meta-ads-search" },
      { label: "TikTok Ads Search", href: "/tools/tiktok-ads-search" },
      { label: "Google Ads Search", href: "/tools/google-ads-search" },
      { label: "Ad Script Generator", href: "/ad-script-generator" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Blog", href: "/blog" },
      { label: "Guides", href: "/guides/how-to-use-meta-ads-library" },
      { label: "Meta Ads Spy Tool", href: "/meta-ads-spy-tool" },
      { label: "TikTok Ads Spy Tool", href: "/tiktok-ads-spy-tool" },
      { label: "Meta Ads Library Alternative", href: "/meta-ads-library-alternative" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms of Service", href: "/terms" },
      { label: "GDPR", href: "/gdpr" },
      { label: "Log in", href: "/login" },
      { label: "Register", href: "/register" },
    ],
  },
];

function Footer({ compact }) {
  if (compact) {
    return (
      <footer className="border-t border-white/[0.06] py-8" data-testid="site-footer">
        <Container>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Link to="/" className="text-sm font-display font-bold text-foreground/70 hover:text-foreground transition-colors">
              RADAR
            </Link>
            <p className="text-xs text-muted-foreground">&copy; {new Date().getFullYear()} RADAR. All rights reserved.</p>
          </div>
        </Container>
      </footer>
    );
  }

  return (
    <footer className="border-t border-white/[0.06] pt-16 pb-8" data-testid="site-footer">
      <Container>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 mb-16">
          <div className="col-span-2 md:col-span-4 lg:col-span-1 mb-4 lg:mb-0">
            <Link to="/" className="mb-4 inline-block text-lg font-display font-bold text-foreground hover:text-lime-400 transition-colors">
              RADAR
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-[260px]">
              Competitor intelligence for Shopify merchants who move fast.
            </p>
          </div>
          {footerColumns.map((col) => (
            <div key={col.title}>
              <h4 className="text-xs font-mono uppercase tracking-wider text-foreground/50 mb-4">{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-white/[0.06] pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground">&copy; {new Date().getFullYear()} RADAR. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Privacy</Link>
            <Link to="/terms" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Terms</Link>
            <Link to="/gdpr" className="text-xs text-muted-foreground hover:text-foreground transition-colors">GDPR</Link>
          </div>
        </div>
      </Container>
    </footer>
  );
}

/**
 * @param {"full" | "legal"} [footer] — full marketing footer (default) or a single legal strip for compact pages (e.g. waitlist).
 */
export default function MarketingLayout({ children, footer = "full" }) {
  const compact = footer === "legal";
  return (
    <div
      className={cn(
        "flex min-h-screen flex-col bg-canvas text-foreground",
        compact && "min-h-dvh"
      )}
    >
      <MarketingNavbar />
      <main className={cn(compact && "flex min-h-0 flex-1 flex-col")}>{children}</main>
      {footer === "full" ? <Footer /> : footer === "legal" ? <Footer compact /> : null}
    </div>
  );
}
